import React, { useEffect, useState, useCallback } from 'react';
import { Activity, Loader2, Server, GitBranch, Users, AlertTriangle, CheckCircle2, Play, Pause, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatApiError } from '@/lib/adminApi';
import { EmptyState } from '@/components/admin/EmptyState';

const STATE_COLOR = {
  DEPLOYED: 'bg-success/15 text-success',
  CONNECTED: 'bg-success/15 text-success',
  UNDEPLOYED: 'bg-white/10 text-white/60',
  DISCONNECTED: 'bg-destructive/15 text-destructive',
};

const Section = ({ icon: Icon, title, count, children }) => (
  <div>
    <h3 className="mb-1 flex items-center gap-2 font-display text-lg font-semibold text-white">
      <Icon className="h-4 w-4 text-brand" /> {title} {count != null && <span className="text-sm font-normal text-white/40">({count})</span>}
    </h3>
    <div className="mt-3">{children}</div>
  </div>
);

export default function AdminMt5() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const fetchData = useCallback(() => {
    api.get('/admin/metaapi/overview').then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const accountAction = async (a, action) => {
    const id = a._id || a.id;
    const prompts = {
      deploy: `Deploy account "${a.name}" (${a.login})? It will start consuming a MetaAPI resource slot.`,
      undeploy: `Undeploy account "${a.name}" (${a.login})? Live copying via this account stops until redeployed.`,
      delete: `PERMANENTLY DELETE account "${a.name}" (${a.login}) from MetaAPI?\n\nThis removes the account and its history from MetaAPI. This cannot be undone.`,
    };
    if (!window.confirm(prompts[action])) return;
    setBusyId(id);
    try {
      if (action === 'delete') await api.delete(`/admin/metaapi/accounts/${id}`);
      else await api.post(`/admin/metaapi/accounts/${id}/${action}`, {});
      toast.success(`Account ${action === 'delete' ? 'deleted' : action + 'ed'}`);
      fetchData();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>;

  if (!data?.connected) {
    return (
      <div className="space-y-6">
        <h1 className="font-display text-2xl font-bold text-white sm:text-3xl">MT5 &amp; Copy Trading</h1>
        <EmptyState icon={Activity} title="MetaAPI not connected" description="Add METAAPI_TOKEN to the backend to monitor real MT5 accounts and CopyFactory activity." />
      </div>
    );
  }

  const { accounts, strategies, subscribers } = data;

  return (
    <div className="space-y-8">
      <div>
        <div className="flex items-center gap-3">
          <h1 className="font-display text-2xl font-bold text-white sm:text-3xl">MT5 &amp; Copy Trading</h1>
          <span className="flex items-center gap-1.5 rounded-full bg-success/15 px-2.5 py-1 text-[11px] font-semibold text-success"><CheckCircle2 className="h-3.5 w-3.5" /> MetaAPI Live</span>
        </div>
        <p className="mt-1 text-sm text-white/50">Real-time data from MetaAPI &amp; CopyFactory for the Copy.Click account.</p>
      </div>

      <Section icon={Server} title="MT5 Accounts" count={accounts.available ? accounts.items.length : null}>
        {accounts.scope_missing ? (
          <div data-testid="admin-mt5-scope-warning" className="flex items-start gap-4 rounded-2xl border border-yellow-400/20 bg-yellow-400/5 p-5">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-yellow-400/10 text-yellow-400"><AlertTriangle className="h-5 w-5" /></span>
            <div>
              <p className="font-display text-base font-semibold text-white">Token is missing the account-management scope</p>
              <p className="mt-1 text-sm leading-relaxed text-white/60">
                Your MetaAPI token works for CopyFactory, but can't list MT5 accounts. Fix: go to <span className="text-brand">app.metaapi.cloud</span> → top-right menu → <span className="text-white">API access token</span> → generate a new token with <span className="text-white">"Trading account management API"</span> access included (or choose the full-access token), then send it to us.
              </p>
            </div>
          </div>
        ) : accounts.items.length === 0 ? (
          <EmptyState icon={Server} title="No MT5 accounts yet" description="MT5 trading accounts provisioned on MetaAPI will appear here with their live connection status." />
        ) : (
          <div className="overflow-hidden rounded-2xl border border-white/10 bg-surface/70">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-white/10 text-xs uppercase tracking-wider text-white/40">
                  <tr>
                    <th className="px-5 py-3 font-medium">Account</th>
                    <th className="px-5 py-3 font-medium">Login</th>
                    <th className="px-5 py-3 font-medium">Server</th>
                    <th className="px-5 py-3 font-medium">Region</th>
                    <th className="px-5 py-3 font-medium">State</th>
                    <th className="px-5 py-3 font-medium">Connection</th>
                    <th className="px-5 py-3 text-right font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.items.map((a) => (
                    <tr key={a._id || a.id} data-testid="admin-mt5-account-row" className="border-b border-white/5 transition-colors hover:bg-white/[0.03]">
                      <td className="px-5 py-3 text-white">{a.name || '—'}</td>
                      <td className="px-5 py-3 font-mono text-xs text-white/70">{a.login || '—'}</td>
                      <td className="px-5 py-3 text-white/70">{a.server || '—'}</td>
                      <td className="px-5 py-3 text-white/60">{a.region || '—'}</td>
                      <td className="px-5 py-3"><span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${STATE_COLOR[a.state] || 'bg-white/10 text-white/60'}`}>{a.state || '—'}</span></td>
                      <td className="px-5 py-3"><span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${STATE_COLOR[a.connectionStatus] || 'bg-white/10 text-white/60'}`}>{a.connectionStatus || '—'}</span></td>
                      <td className="px-5 py-3">
                        <div className="flex items-center justify-end gap-2">
                          {busyId === (a._id || a.id) ? (
                            <Loader2 className="h-4 w-4 animate-spin text-brand" />
                          ) : (
                            <>
                              {a.state === 'DEPLOYED' ? (
                                <button onClick={() => accountAction(a, 'undeploy')} data-testid="admin-mt5-undeploy" title="Undeploy (stop)" className="flex items-center gap-1.5 rounded-lg border border-white/10 px-2.5 py-1.5 text-xs text-white/70 hover:bg-white/5">
                                  <Pause className="h-3.5 w-3.5" /> Undeploy
                                </button>
                              ) : (
                                <button onClick={() => accountAction(a, 'deploy')} data-testid="admin-mt5-deploy" title="Deploy (start)" className="flex items-center gap-1.5 rounded-lg border border-success/30 px-2.5 py-1.5 text-xs text-success hover:bg-success/10">
                                  <Play className="h-3.5 w-3.5" /> Deploy
                                </button>
                              )}
                              <button onClick={() => accountAction(a, 'delete')} data-testid="admin-mt5-delete" title="Delete account from MetaAPI" className="rounded-lg border border-white/10 p-1.5 text-white/50 hover:bg-destructive/10 hover:text-destructive">
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Section>

      <Section icon={GitBranch} title="CopyFactory Strategies" count={strategies.length}>
        {strategies.length === 0 ? (
          <EmptyState icon={GitBranch} title="No strategies configured" description="When signal-provider strategies are created in CopyFactory, they'll show up here with their settings." />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {strategies.map((s) => (
              <div key={s._id} data-testid="admin-cf-strategy-card" className="rounded-2xl border border-white/10 bg-surface/70 p-5">
                <p className="font-display text-base font-semibold text-white">{s.name}</p>
                <p className="mt-1 font-mono text-[11px] text-white/40">{s._id}</p>
                {s.description && <p className="mt-2 text-xs text-white/50">{s.description}</p>}
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section icon={Users} title="CopyFactory Subscribers" count={subscribers.length}>
        {subscribers.length === 0 ? (
          <EmptyState icon={Users} title="No copy-trading subscribers yet" description="Followers copying strategies via CopyFactory will appear here with their subscriptions." />
        ) : (
          <div className="overflow-hidden rounded-2xl border border-white/10 bg-surface/70">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-white/10 text-xs uppercase tracking-wider text-white/40">
                <tr>
                  <th className="px-5 py-3 font-medium">Subscriber</th>
                  <th className="px-5 py-3 font-medium">Strategies</th>
                </tr>
              </thead>
              <tbody>
                {subscribers.map((s) => (
                  <tr key={s._id} data-testid="admin-cf-subscriber-row" className="border-b border-white/5 hover:bg-white/[0.03]">
                    <td className="px-5 py-3 text-white">{s.name || s._id}</td>
                    <td className="px-5 py-3 text-white/60">{(s.subscriptions || []).map((x) => x.strategyId).join(', ') || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}
